// Auto-extracted from SellerAnalytics.jsx
import { useState } from "react";
import { C } from "../../lib/theme";
import { Card } from "../../components/Card";
import { Icon } from "../../components/Icon";
import { fmtK, fmtKShort } from "../../lib/money";
import { resolveImage } from "../../lib/images";
import { niceMax } from "./constants";

export function RevenueChart({ series }) {
  const [selected, setSelected] = useState(null);
  const max = Math.max(1, ...series.map((d) => d.revenue));
  const axisMax = niceMax(max);
  const width = 720;
  const height = 200;
  const padLeft = 44;
  const padBottom = 22;
  const padTop = 10;
  const innerW = width - padLeft - 8;
  const innerH = height - padTop - padBottom;
  const barGap = 3;
  const barW = Math.max(4, (innerW - barGap * (series.length - 1)) / Math.max(1, series.length));

  const gridLines = [0, 0.25, 0.5, 0.75, 1];
  const stride = Math.max(1, Math.ceil(series.length / 8));

  const bars = series.map((d, i) => {
    const x = padLeft + i * (barW + barGap);
    const h = axisMax > 0 ? (d.revenue / axisMax) * innerH : 0;
    const y = padTop + innerH - h;
    return { x, y, h, d, i };
  });

  const sel = selected != null ? bars[selected] : null;

  return (
    <div style={{ overflowX: "auto", position: "relative" }}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        style={{ display: "block", overflow: "visible" }}
      >
        {gridLines.map((g) => {
          const y = padTop + innerH * (1 - g);
          return (
            <g key={g}>
              <line x1={padLeft} x2={width - 4} y1={y} y2={y} stroke={C.cream} strokeWidth={1} />
              <text x={padLeft - 6} y={y + 3} fontSize={9} fill={C.muted} textAnchor="end" fontWeight={700}>
                {fmtKShort(axisMax * g)}
              </text>
            </g>
          );
        })}

        {bars.map((b) => {
          const isSelected = selected === b.i;
          const showLabel = b.i === 0 || b.i === series.length - 1 || b.i % stride === 0;
          return (
            <g key={b.d.key}>
              <rect
                x={b.x}
                y={b.y}
                width={barW}
                height={b.h}
                rx={Math.min(3, barW / 2)}
                fill={isSelected ? C.hunterDk : C.hunter}
                style={{ cursor: "pointer", transition: "fill .15s ease" }}
                onClick={() => setSelected(selected === b.i ? null : b.i)}
              >
                <title>{`${b.d.label} · ${fmtK(b.d.revenue)} · ${b.d.orders} orders`}</title>
              </rect>
              {/* Wider invisible hit target for thin bars */}
              {barW < 10 && (
                <rect
                  x={b.x - (10 - barW) / 2}
                  y={padTop}
                  width={10}
                  height={innerH}
                  fill="transparent"
                  style={{ cursor: "pointer" }}
                  onClick={() => setSelected(selected === b.i ? null : b.i)}
                />
              )}
              {showLabel && (
                <text
                  x={b.x + barW / 2}
                  y={height - 6}
                  fontSize={9}
                  fill={C.muted}
                  textAnchor="middle"
                  fontWeight={700}
                >
                  {b.d.label}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      {sel && (
        <div
          style={{
            position: "absolute",
            left: `${((sel.x + barW / 2) / width) * 100}%`,
            top: `${(sel.y / height) * 100}%`,
            transform: "translate(-50%, calc(-100% - 8px))",
            background: C.charcoal,
            color: C.white,
            padding: "7px 11px",
            borderRadius: 8,
            fontSize: 11,
            fontWeight: 700,
            whiteSpace: "nowrap",
            pointerEvents: "none",
            boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
            zIndex: 5,
          }}
        >
          <div style={{ fontSize: 9.5, fontWeight: 600, opacity: 0.7, marginBottom: 2 }}>{sel.d.label}</div>
          <div style={{ marginBottom: 1 }}>{fmtK(sel.d.revenue)}</div>
          <div style={{ fontSize: 10, fontWeight: 600, opacity: 0.8 }}>
            {sel.d.orders} {sel.d.orders === 1 ? "order" : "orders"}
          </div>
        </div>
      )}
    </div>
  );
}

